/**
 * Number formatting utilities for consistent display throughout the app.
 * All formatters use Intl.NumberFormat for locale-aware output.
 */

// ─── CURRENCY ───

/** Format as currency: $1,499 (no decimals) */
export function formatPrice(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/** Format as currency with decimals: $1,499.00 */
export function formatCurrencyExact(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Format large currency values compactly: $1.39M, $440K */
export function formatCurrencyCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : value > 0 ? '+' : '';

  if (abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  }
  return `${sign}$${abs.toFixed(0)}`;
}

/** Format P&L with sign and color class: "+$440K" or "-$537K" */
export function formatPnL(value: number): { text: string; className: string } {
  const text = formatCurrencyCompact(value);
  const className = value >= 0 ? 'text-metric-positive' : 'text-metric-negative';
  return { text, className };
}

/** Format P&L with explicit sign: +$440,123 or ($537,456) */
export function formatPnLExact(value: number): string {
  if (value >= 0) {
    return `+${formatPrice(value)}`;
  }
  return `(${formatPrice(Math.abs(value))})`;
}


// ─── PERCENTAGES ───

/** Format as percentage with 1 decimal: 23.0% */
export function formatPercent(value: number, decimals: number = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/** Format as percentage with sign: +13.2% or -5.3% */
export function formatPercentSigned(value: number, decimals: number = 1): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(decimals)}%`;
}

/** Format as basis points change: +22.3pp */
export function formatBasisPoints(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(1)}pp`;
}


// ─── NUMBERS ───

/** Format with comma separators: 1,221 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(Math.round(value));
}

/** Format number with sign: +1,221 or -537 */
export function formatNumberSigned(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatNumber(value)}`;
}

/** Format with specified decimals: 0.8500 */
export function formatDecimal(value: number, decimals: number = 4): string {
  return value.toFixed(decimals);
}


// ─── MULTIPLIERS ───

/** Format as multiplier: 2.4x */
export function formatMultiplier(value: number): string {
  return `${value.toFixed(1)}x`;
}


// ─── UTILITY ───

/** Get CSS class for P&L color coding */
export function pnlColorClass(value: number): string {
  if (value > 0) return 'text-metric-positive';
  if (value < 0) return 'text-metric-negative';
  return 'text-gray-400';
}

/** Get CSS class for risk level badges */
export function riskBadgeClass(level: 'HIGH' | 'MEDIUM' | 'LOW'): string {
  switch (level) {
    case 'HIGH': return 'badge-high';
    case 'MEDIUM': return 'badge-medium';
    case 'LOW': return 'badge-low';
  }
}

/** Clamp a value between min and max */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
